import { ReactPlugin } from '@microsoft/applicationinsights-react-js'
import {
  ApplicationInsights,
  IEventTelemetry,
  IExceptionTelemetry,
  IMetricTelemetry,
  ITraceTelemetry,
} from '@microsoft/applicationinsights-web'

import config from '@app/config'

import { correlationHeaderDomains } from './correlationHeaderDomains'
import { excludedDomains } from './excludedDomains'
import history from './history'
import {
  LogEventProperties,
  LogMetricProperties,
  LogTraceProperties,
} from './types'

const APP_VERSION = import.meta.env.VITE_APP_VERSION ?? 'unknown'

const reactPlugin = new ReactPlugin()
const appInsights = new ApplicationInsights({
  config: {
    instrumentationKey: config.variables.appInsightsKey,
    enableAutoRouteTracking: true,
    enableCorsCorrelation: true,
    correlationHeaderDomains: correlationHeaderDomains,
    correlationHeaderExcludedDomains: excludedDomains,
    excludeRequestFromAutoTrackingPatterns: excludedDomains,
    extensions: [reactPlugin],
    extensionConfig: {
      [reactPlugin.identifier]: { history: history },
    },
  },
})

if (document.location.hostname !== 'localhost') {
  appInsights.loadAppInsights()
  appInsights.context.application.ver = APP_VERSION

  const replacePattern = /https?:\/\/.*\.azurewebsites\.net/
  const pathToChat = `${document.location.origin}/messages/`

  appInsights.addTelemetryInitializer((envelope) => {
    const telemetryItem = envelope.baseData

    if (telemetryItem) {
      const telemetryWithProperties = telemetryItem as {
        properties?: Record<string, unknown>
      }
      telemetryWithProperties.properties = {
        ...(telemetryWithProperties.properties ?? {}),
        appVersion: APP_VERSION,
      }

      telemetryItem.uri = document.location.href

      if (telemetryItem.url) {
        telemetryItem.url = telemetryItem.url.replace(
          replacePattern,
          pathToChat
        )
      }

      if (telemetryItem.target) {
        telemetryItem.target = telemetryItem.target.replace(
          replacePattern,
          pathToChat
        )
      }
    }

    envelope.tags = {
      ...(envelope.tags ?? {}),
      'ai.application.ver': APP_VERSION,
    }
  })
}

const logPageView = (name?: string, url?: string): void => {
  appInsights.trackPageView(
    {
      name: name,
      uri: url,
    },
    {
      appVersion: APP_VERSION,
    }
  )
}

const logEvent = (name: string, properties?: LogEventProperties): void => {
  const propsWithVersion: LogEventProperties = {
    ...(properties ?? {}),
    appVersion: APP_VERSION,
  }
  appInsights.trackEvent({ name: name } as IEventTelemetry, propsWithVersion)
}

const logMetric = (
  name: string,
  average: number,
  properties?: LogMetricProperties
): void => {
  const propsWithVersion: LogMetricProperties = {
    ...(properties ?? {}),
    appVersion: APP_VERSION,
  }
  appInsights.trackMetric(
    { name: name, average: average } as IMetricTelemetry,
    propsWithVersion
  )
}

const logException = (exception: Error, severityLevel?: number): void => {
  appInsights.trackException(
    {
      exception: exception,
      severityLevel: severityLevel,
    } as IExceptionTelemetry,
    {
      appVersion: APP_VERSION,
    }
  )
}

const logTrace = (message: string, properties?: LogTraceProperties): void => {
  const propsWithVersion: LogTraceProperties = {
    ...(properties ?? {}),
    appVersion: APP_VERSION,
  }
  appInsights.trackTrace(
    { message: message } as ITraceTelemetry,
    propsWithVersion
  )
}

export {
  reactPlugin,
  appInsights,
  logPageView,
  logEvent,
  logMetric,
  logException,
  logTrace,
}
